import { useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FacebookIcon, InstagramIcon } from '@/components/icons/platform-icons';
import { VISIBLE_CHANNEL_CHIPS, channelLabel } from '../constants';
import type { SocialPlatform, SocialRuleChannelScope } from '../types';

/** One channel a rule is scoped to, with its handle already resolved for display. */
interface ScopedChannel {
	channelId: string;
	platform: SocialPlatform;
	handle: string;
}

/** channelId → what to call it, built from the connected Meta accounts. */
export type ChannelHandleMap = Map<string, { handle: string; name: string; platform: 'facebook' | 'instagram' }>;

interface ChannelChipsProps {
	/** Empty means the rule applies to every connected channel. */
	channelScope: SocialRuleChannelScope[];
	handles: ChannelHandleMap;
	/** Every connected channel, used to spell out what "all channels" currently means. */
	allChannels: { channelId: string; handle: string; name: string; platform: 'facebook' | 'instagram' }[];
}

const PlatformIcon = ({ platform }: { platform: 'facebook' | 'instagram' }) =>
	platform === 'facebook' ? (
		<FacebookIcon className='size-3.5 shrink-0' />
	) : (
		<InstagramIcon className='size-3.5 shrink-0' />
	);

const Chip = ({ platform, handle }: { platform: 'facebook' | 'instagram'; handle: string }) => (
	<span className='flex items-center gap-1.5 rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-900'>
		<PlatformIcon platform={platform} />
		<span className='truncate max-w-[110px]'>{handle}</span>
	</span>
);

/**
 * The accounts a rule acts on.
 *
 * An EMPTY scope is a wildcard, not a selection. The engine reads it as "every connected channel",
 * and this used to spell that out by listing them all — which made a rule nobody scoped look
 * identical to one where somebody ticked every box, and read as though the form had silently
 * chosen for you. It now says "All channels" and keeps the enumeration in the tooltip, so the
 * distinction is visible and the detail is still one hover away.
 *
 * That difference matters beyond looks: a wildcard rule picks up any Page connected later, while
 * an explicit list does not.
 *
 * A channel in scope that is no longer connected still renders — as its raw id rather than being
 * dropped. Silently hiding it would make a rule look narrower than it is, and the id is the only
 * thing left to identify it by once the account is gone.
 */
/** The order the design lists the groups in: Instagram first, Facebook second. */
const PLATFORM_ORDER: { platform: SocialPlatform; label: string }[] = [
	{ platform: 'instagram', label: 'Instagram' },
	{ platform: 'facebook', label: 'Facebook' }
];

/**
 * What the "+N" chip opens (Figma 4235:73590).
 *
 * A popover grouped by platform, not a tooltip listing handles. The difference matters once a rule
 * spans both networks: a flat list makes the reader work out which handle belongs to which, and
 * "acmes" exists on both. It is also selectable text a tooltip would snatch away on mouse-out.
 */
const OverflowPopover = ({ channels, hiddenCount }: { channels: ScopedChannel[]; hiddenCount: number }) => {
	const [open, setOpen] = useState(false);
	// Closing is delayed so the pointer can cross the gap between the chip and the panel without
	// the panel vanishing underneath it.
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const cancelClose = () => {
		if (closeTimer.current) clearTimeout(closeTimer.current);
		closeTimer.current = null;
	};

	const openNow = () => {
		cancelClose();
		setOpen(true);
	};

	const closeSoon = () => {
		cancelClose();
		closeTimer.current = setTimeout(() => setOpen(false), 120);
	};

	useEffect(() => cancelClose, []);

	const groups = PLATFORM_ORDER.map((group) => ({
		...group,
		channels: channels.filter((channel) => channel.platform === group.platform)
	})).filter((group) => group.channels.length > 0);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				{/*
				 * Opens on HOVER. Still a button, and still opens on click and focus: hover alone
				 * would leave the list unreachable by keyboard and on touch, where there is no hover
				 * at all.
				 */}
				<button
					type='button'
					aria-label={`Show all ${channels.length} channels`}
					className='flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-900'
					onMouseEnter={openNow}
					onMouseLeave={closeSoon}
					onFocus={openNow}
					onBlur={closeSoon}
					onClick={(event) => event.stopPropagation()}
				>
					+{hiddenCount}
				</button>
			</PopoverTrigger>
			<PopoverContent
				align='start'
				onMouseEnter={openNow}
				onMouseLeave={closeSoon}
				onOpenAutoFocus={(event) => event.preventDefault()}
				onClick={(event) => event.stopPropagation()}
				className='w-auto min-w-[180px] rounded-2xl border-border/60 bg-white/80 p-2 shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] backdrop-blur-[12px]'
			>
				{groups.map((group, index) => (
					<div key={group.platform}>
						{index > 0 && <div className='my-2 h-px bg-gray-300' />}
						<p className='px-2 py-1 text-[11px] leading-4 text-gray-800'>{group.label}</p>
						{group.channels.map((channel) => (
							<p key={channel.channelId} className='px-2 py-1 text-[13px] leading-[18px] text-ink'>
								{channel.handle}
							</p>
						))}
					</div>
				))}
			</PopoverContent>
		</Popover>
	);
};

export const ChannelChips = ({ channelScope, handles, allChannels }: ChannelChipsProps) => {
	if (channelScope.length === 0) {
		if (allChannels.length === 0) {
			return <span className='text-xs text-muted-foreground'>No channels connected</span>;
		}

		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<span className='flex w-fit items-center rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-900 cursor-default'>
						All channels
					</span>
				</TooltipTrigger>
				<TooltipContent variant="dark" className='flex flex-col gap-0.5'>
					{allChannels.map((channel) => (
						<span key={channel.channelId}>{channelLabel(channel)}</span>
					))}
					{/*
					 * The list is what "all channels" means TODAY, and reading it as the rule's fixed
					 * scope is the misunderstanding worth heading off: an unscoped rule is re-matched
					 * against the account's channels on every comment, so a Page connected next month
					 * is in scope the moment it is connected, without anyone re-saving the rule.
					 */}
					{/*
					 * white/70 rather than a `text-muted-*` token: the tooltip's dark variant sets a
					 * flat `text-white`, and the muted tokens are the light theme's greys, which sit
					 * near-invisible on #2b2e34.
					 */}
					<span className='mt-1 border-t border-white/20 pt-1 text-white/70'>
						and any channel connected later
					</span>
				</TooltipContent>
			</Tooltip>
		);
	}

	const scoped = channelScope.map((scope) => ({
		channelId: scope.channelId,
		platform: handles.get(scope.channelId)?.platform ?? scope.platform,
		handle: (() => {
			const known = handles.get(scope.channelId);
			return known ? channelLabel({ ...known, channelId: scope.channelId }) : scope.channelId;
		})()
	}));

	const visible = scoped.slice(0, VISIBLE_CHANNEL_CHIPS);
	const hidden = scoped.slice(VISIBLE_CHANNEL_CHIPS);

	return (
		<div className='flex items-center gap-1.5 min-w-0'>
			{visible.map((channel) => (
				<Chip key={channel.channelId} platform={channel.platform} handle={channel.handle} />
			))}
			{/*
			 * The popover lists EVERY channel the rule runs on, not only the ones that did not fit.
			 * "+1" answers "how many more are there"; opening it answers "which channels is this
			 * rule on", and that question includes the three already on screen. Reading a partial
			 * list as the whole scope is the mistake worth preventing.
			 */}
			{hidden.length > 0 && <OverflowPopover channels={scoped} hiddenCount={hidden.length} />}
		</div>
	);
};
